import { useState } from 'react'
import { Button } from '../../components/Button'
import { Card } from '../../components/Card'
import { Modal } from '../../components/Modal'
import { ToastProvider, useToast } from '../../components/Toast'
import { useDb } from '../../state/DbProvider'
import { getCurrentUser } from '../../state/selectors'
import type { Product } from '../../state/types'
import { formatPercent } from '../../utils/format'
import { getProductPriceLabel, inferProductTypeFromName, parsePriceInputJPY, sanitizePriceInput } from '../../utils/products'

type ProductDraft = {
  id?: string
  name: string
  listPriceJPY: string
  description: string
  agencyRate: string
  connectorRate: string
}

function formatRateInput(rate?: number) {
  if (rate === undefined) return ''
  return String(Math.round(rate * 10000) / 100)
}

function parseRateInput(value: string): number | null | undefined {
  const trimmed = value.trim()
  if (!trimmed) return null
  const parsed = Number(trimmed)
  if (!Number.isFinite(parsed)) return undefined
  return parsed / 100
}

function toDraft(product?: Product): ProductDraft {
  if (!product) {
    return { name: '', listPriceJPY: '', description: '', agencyRate: '', connectorRate: '' }
  }

  return {
    id: product.id,
    name: product.name,
    listPriceJPY: product.listPriceJPY > 0 ? String(product.listPriceJPY) : '',
    description: product.description,
    agencyRate: formatRateInput(product.agencyRate),
    connectorRate: formatRateInput(product.connectorRate),
  }
}

function AdminProductsInner() {
  const { db, actions } = useDb()
  const toast = useToast()
  const [draft, setDraft] = useState<ProductDraft | null>(null)
  const [isCreate, setIsCreate] = useState(false)

  if (!db) return null
  const me = getCurrentUser(db)

  if (me.role !== 'J-Navi管理者') {
    return (
      <Card title="商品マスター" subtitle="このページは J-Navi 管理者向けです">
        <div className="px-5 py-4 text-sm text-slate-600">権限がありません。</div>
      </Card>
    )
  }

  const defaultSupplierId = db.suppliers[0]?.id
  const products = [...db.products].sort((a, b) => a.name.localeCompare(b.name, 'ja'))

  const openCreate = () => {
    setIsCreate(true)
    setDraft(toDraft())
  }

  const openEdit = (product: Product) => {
    setIsCreate(false)
    setDraft(toDraft(product))
  }

  const closeModal = () => setDraft(null)

  return (
    <div className="grid gap-4">
      <Card
        title="商品マスター"
        subtitle="商品名・金額・説明の 3 項目で管理します。金額は円単位の数字で保存します。"
        right={
          <Button variant="primary" onClick={openCreate} disabled={!defaultSupplierId}>
            商品を追加
          </Button>
        }
      >
        <div className="px-5 py-4">
          {!defaultSupplierId ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
              仕入先マスターが未登録のため、商品を追加できません。
            </div>
          ) : null}

          <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-left text-slate-600">
                <tr>
                  <th className="px-4 py-3">商品名</th>
                  <th className="px-4 py-3">金額</th>
                  <th className="px-4 py-3">説明</th>
                  <th className="px-4 py-3">報酬率</th>
                  <th className="px-4 py-3 text-right">操作</th>
                </tr>
              </thead>
              <tbody>
                {products.map((product) => (
                  <tr key={product.id} className="border-t border-slate-100">
                    <td className="px-4 py-3 font-semibold text-slate-900">{product.name}</td>
                    <td className="px-4 py-3 text-slate-700">{getProductPriceLabel(product)}</td>
                    <td className="px-4 py-3 text-slate-600">
                      <div className="max-w-xl whitespace-pre-wrap break-words">{product.description || '-'}</div>
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-600">
                      {product.agencyRate !== undefined
                        ? `総率 ${formatPercent(product.agencyRate)} / CN ${formatPercent(product.connectorRate ?? 0)}`
                        : '既定'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-2">
                        <Button size="sm" variant="secondary" onClick={() => openEdit(product)}>
                          編集
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={async () => {
                            if (!window.confirm(`「${product.name}」を削除しますか？`)) return
                            const result = await actions.deleteProduct(product.id)
                            if (!result.ok) {
                              toast.show(result.message ?? '商品を削除できませんでした', 'error')
                              return
                            }
                            toast.show('商品を削除しました', 'success')
                          }}
                        >
                          削除
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
                {products.length === 0 ? (
                  <tr>
                    <td className="px-4 py-6 text-center text-sm text-slate-500" colSpan={5}>
                      商品はまだ登録されていません。
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </div>
      </Card>

      <Modal
        open={!!draft}
        onClose={closeModal}
        title={isCreate ? '商品を追加' : '商品を編集'}
        description="カテゴリ入力は廃止しました。金額は円単位の数字のみ保存できます。"
      >
        {draft ? (
          <div className="grid gap-4">
            <label className="grid gap-1">
              <span className="text-sm text-slate-700">商品名</span>
              <input
                value={draft.name}
                onChange={(event) => setDraft((current) => (current ? { ...current, name: event.target.value } : current))}
                className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
                placeholder="例: サイネージ"
              />
            </label>

            <label className="grid gap-1">
              <span className="text-sm text-slate-700">金額（円）</span>
              <input
                value={draft.listPriceJPY}
                onChange={(event) =>
                  setDraft((current) =>
                    current
                      ? { ...current, listPriceJPY: sanitizePriceInput(event.target.value) }
                      : current,
                  )
                }
                inputMode="numeric"
                className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
                placeholder="例: 1200000"
              />
            </label>

            <label className="grid gap-1">
              <span className="text-sm text-slate-700">説明</span>
              <textarea
                value={draft.description}
                onChange={(event) =>
                  setDraft((current) => (current ? { ...current, description: event.target.value } : current))
                }
                className="min-h-32 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
                placeholder="商品説明を入力"
              />
            </label>

            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
              <div className="text-xs font-medium text-slate-700 mb-2">報酬率（商品別・任意）</div>
              <div className="text-xs text-slate-500 mb-3">
                空欄の場合はユーザー個別設定またはシステム全体設定の報酬率が適用されます。
              </div>
              <div className="grid grid-cols-2 gap-3">
                <label className="grid gap-1">
                  <span className="text-xs text-slate-700">代理店総率（%）</span>
                  <input
                    value={draft.agencyRate}
                    onChange={(event) =>
                      setDraft((current) => (current ? { ...current, agencyRate: event.target.value } : current))
                    }
                    inputMode="decimal"
                    className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
                    placeholder="例: 15"
                  />
                </label>
                <label className="grid gap-1">
                  <span className="text-xs text-slate-700">コネクター率（%）</span>
                  <input
                    value={draft.connectorRate}
                    onChange={(event) =>
                      setDraft((current) => (current ? { ...current, connectorRate: event.target.value } : current))
                    }
                    inputMode="decimal"
                    className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
                    placeholder="例: 5"
                  />
                </label>
              </div>
            </div>

            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
              内部種別の自動判定:
              「ホテル」「会員権」を含む商品名はホテル会員権扱い、
              「広告」を含む商品名は広告枠扱い、
              それ以外は通常商材扱いになります。
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={closeModal}>
                キャンセル
              </Button>
              <Button
                variant="primary"
                onClick={async () => {
                  if (!draft.name.trim()) {
                    toast.show('商品名を入力してください', 'error')
                    return
                  }
                  if (!defaultSupplierId) {
                    toast.show('仕入先マスターが未登録です', 'error')
                    return
                  }
                  const listPriceJPY = parsePriceInputJPY(draft.listPriceJPY)
                  if (listPriceJPY <= 0) {
                    toast.show('金額は 1 円以上の数字で入力してください', 'error')
                    return
                  }

                  const parsedAgencyRate = parseRateInput(draft.agencyRate)
                  const parsedConnectorRate = parseRateInput(draft.connectorRate)
                  if (parsedAgencyRate === undefined) {
                    toast.show('代理店報酬率は数値で入力してください', 'error')
                    return
                  }
                  if (parsedConnectorRate === undefined) {
                    toast.show('コネクター報酬率は数値で入力してください', 'error')
                    return
                  }
                  if (parsedAgencyRate !== null && (parsedAgencyRate < 0 || parsedAgencyRate > 1)) {
                    toast.show('代理店報酬率は 0 から 100 の範囲で入力してください', 'error')
                    return
                  }
                  if (parsedConnectorRate !== null && (parsedConnectorRate < 0 || parsedConnectorRate > 1)) {
                    toast.show('コネクター報酬率は 0 から 100 の範囲で入力してください', 'error')
                    return
                  }
                  if (parsedAgencyRate !== null && parsedConnectorRate !== null && parsedConnectorRate > parsedAgencyRate) {
                    toast.show('コネクター報酬率は代理店総率以下にしてください', 'error')
                    return
                  }
                  if (parsedAgencyRate !== null && parsedAgencyRate > db.settings.superAgencyRate) {
                    toast.show('代理店報酬率は総代理店総率以下にしてください', 'error')
                    return
                  }

                  const payload = {
                    supplierId: defaultSupplierId,
                    name: draft.name.trim(),
                    description: draft.description.trim(),
                    type: inferProductTypeFromName(draft.name),
                    listPriceJPY,
                    materials: [],
                    isPublic: true,
                    category: undefined,
                    agencyRate: parsedAgencyRate ?? undefined,
                    connectorRate: parsedConnectorRate ?? undefined,
                  }

                  const result = isCreate
                    ? await actions.createProduct(payload)
                    : await actions.updateProduct(draft.id!, {
                        name: payload.name,
                        description: payload.description,
                        listPriceJPY: payload.listPriceJPY,
                        agencyRate: parsedAgencyRate ?? undefined,
                        connectorRate: parsedConnectorRate ?? undefined,
                      })

                  if (!result.ok) {
                    toast.show(result.message ?? '商品を保存できませんでした', 'error')
                    return
                  }

                  toast.show(isCreate ? '商品を追加しました' : '商品を更新しました', 'success')
                  closeModal()
                }}
              >
                保存
              </Button>
            </div>
          </div>
        ) : null}
      </Modal>
    </div>
  )
}

export default function AdminProductsPage() {
  return (
    <ToastProvider>
      <AdminProductsInner />
    </ToastProvider>
  )
}
